import { omit } from "lodash";
import { isCourseSpace } from "@/react/helpers/spaceHelpers";

const getNotificationTypeArray = space => [
  ...(space.default_notification_setting == "all"
    ? ["default_notification_setting"]
    : []),
  ...(space.default_in_app_notification_setting == "all"
    ? ["default_in_app_notification_setting"]
    : []),
  ...(space.default_mobile_notification_setting == "all"
    ? ["default_mobile_notification_setting"]
    : []),
];

const getChatNotificationType = space => {
  if (space.all_notifications_enabled) {
    return "all";
  }
  if (space.mention_notifications_enabled) {
    return "mentions";
  }
  if (space.notifications_off) {
    return "never";
  }
};

const getNotificationTypePayload = notification_type => ({
  default_notification_setting: notification_type.includes(
    "default_notification_setting",
  )
    ? "all"
    : "never",
  default_in_app_notification_setting: notification_type.includes(
    "default_in_app_notification_setting",
  )
    ? "all"
    : "never",
  default_mobile_notification_setting: notification_type.includes(
    "default_mobile_notification_setting",
  )
    ? "all"
    : "never",
  default_mention_mobile_notification_setting: notification_type.includes(
    "default_mention_mobile_notification_setting",
  )
    ? "all"
    : "never",
  default_mention_in_app_notification_setting: notification_type.includes(
    "default_mention_in_app_notification_setting",
  )
    ? "all"
    : "never",
});

const getVisibleTabsArray = ({ visible_tabs = {} }) =>
  Object.keys(visible_tabs).filter(key => visible_tabs[key]);

const courseSettingAttributes = space =>
  isCourseSpace(space)
    ? { course_setting_attributes: space.course_setting }
    : {};

export const parseSpaceResponse = (space = {}) => ({
  ...omit(space, [
    "default_notification_setting",
    "default_in_app_notification_setting",
    "default_mobile_notification_setting",
    "visible_tabs",
    "hide_right_sidebar",
    "is_post_disabled",
    "prevent_members_from_adding_others",
    "hide_post_settings",
    "disable_member_post_covers",
    "is_hidden",
    "hide_sorting",
    "course_setting",
    "require_topic_selection",
  ]),
  notification_type:
    space.post_type === "chat"
      ? getChatNotificationType(space)
      : getNotificationTypeArray(space),
  show_right_sidebar: !space.hide_right_sidebar,
  allow_members_to_create_posts: !space.is_post_disabled,
  allow_members_to_add_others: !space.prevent_members_from_adding_others,
  allow_post_title: !space.hide_post_settings,
  allow_cover_images: !space.disable_member_post_covers,
  show_sorting: !space.hide_sorting,
  show_to_logged_out_visitors: !space.is_hidden,
  visible_tabs: getVisibleTabsArray(space),
  locked_page_description: space.locked_page_description?.trix_html,
  ...courseSettingAttributes(space),
  ...welcomeBannerAttributes(space),
  topics: space.topics_order,
  require_topic_selection: space.require_topic_selection,
});

const welcomeBannerAttributes = space => {
  let data = {};
  if (space.logged_in_space_welcome_banner) {
    data = {
      logged_in_title: space.logged_in_space_welcome_banner.title,
      logged_in_description: {
        ...(space.logged_in_space_welcome_banner.description || {}),
      },
      logged_in_cover: space.logged_in_space_welcome_banner.cover,
      logged_in_banner_enabled:
        space.logged_in_space_welcome_banner.banner_enabled,
      allow_close: space.logged_in_space_welcome_banner.allow_close,
      logged_in_media_is_video: space.logged_in_space_welcome_banner.is_video,
    };
  }
  if (space.logged_out_space_welcome_banner) {
    data = {
      ...data,
      logged_out_title: space.logged_out_space_welcome_banner.title,
      logged_out_description: {
        ...(space.logged_out_space_welcome_banner.description || {}),
      },
      logged_out_cover: space.logged_out_space_welcome_banner.cover,
      logged_out_banner_enabled:
        space.logged_out_space_welcome_banner.banner_enabled,
      logged_out_media_is_video: space.logged_out_space_welcome_banner.is_video,
    };
  }
  return data;
};

const getVisibleTabsPayload = visible_tabs => {
  const visibleTabsPayload = {};
  visible_tabs.forEach(key => (visibleTabsPayload[key] = true));
  return visibleTabsPayload;
};

const mapChatNotificationPreference = value => {
  if (value.includes("all")) {
    return [
      "default_mobile_notification_setting",
      "default_mention_mobile_notification_setting",
      "default_mention_in_app_notification_setting",
    ];
  }
  if (value.includes("mentions")) {
    return [
      "default_mention_mobile_notification_setting",
      "default_mention_in_app_notification_setting",
    ];
  }
  return [];
};

export const parseSpacePayload = payload => {
  const {
    show_right_sidebar,
    allow_members_to_create_posts,
    allow_members_to_add_others,
    allow_post_title,
    allow_cover_images,
    show_to_logged_out_visitors,
    meta_tag_attributes,
    notification_type,
    visible_tabs,
    show_sorting,
  } = payload;

  return {
    ...omit(payload, [
      "default_notification_setting",
      "default_in_app_notification_setting",
      "default_mobile_notification_setting",
      "visible_tabs",
      "show_right_sidebar",
      "allow_members_to_create_posts",
      "allow_members_to_add_others",
      "allow_post_title",
      "allow_cover_images",
      "show_to_logged_out_visitors",
      "customize_lock_screen",
      "policies",
      "is_space_owner",
      "is_space_member",
      "meta_tag_attributes",
      "show_sorting",
    ]),
    hide_right_sidebar: !show_right_sidebar,
    is_post_disabled: !allow_members_to_create_posts,
    prevent_members_from_adding_others: !allow_members_to_add_others,
    hide_post_settings: !allow_post_title,
    disable_member_post_covers: !allow_cover_images,
    is_hidden: !show_to_logged_out_visitors,
    hide_sorting: !show_sorting,
    visible_tabs: getVisibleTabsPayload(visible_tabs),
    ...getNotificationTypePayload(
      payload.post_type === "chat"
        ? mapChatNotificationPreference(notification_type)
        : notification_type,
    ),
    meta_tag_attributes: {
      ...omit(meta_tag_attributes, "opengraph_image_id"),
    },
  };
};
